/**
 * Pure stretch-math module for editable wall measurements.
 *
 * Drives geometry from `structure.walls` (the canonical wall set) and
 * cascades to room walls via `snapRoomWallToStructure` (per-end offsets —
 * preserves miter insets at their fixed length, unlike `rewrapRoomWall`'s
 * proportional mapping which scales miters with the wall length).
 *
 * Stretch is one *displacement field* over the junction graph: every
 * junction inside the translate-rectangle moves by `delta * dir`, the rest
 * stay. `cascadeDisplacement` (utils/wallDisplacement.js) turns that field
 * into the derived half of the plan, and stays general so the freehand
 * gestures can come back through it.
 *
 * Child positioning (doors / windows / openings / objects) is uniform:
 * classify the child's `center` projection along the stretch axis vs
 * `boundary` — moving side translates by `delta * dir`, static side
 * stays. The user controls what moves by dragging the boundary.
 *
 * No Firestore, no React. The same plan is used for both the live
 * preview overlay and the final save, so previewed and persisted state
 * cannot drift apart.
 */

import { getMeasurementKey } from './measurementKeys';
import { apply3DRotationTo2D } from './geometryUtils';
import {
  resolveStretchStoryScope,
  countStoryScope,
  reachableStructureForMeasurement,
  scopeStructureForMeasurement,
} from './stretchStoryScope';
import {
  defaultStretchRegion,
  resnapCut,
  stretchRegionBounds,
  structuralCutRanges,
} from './stretchRegion';
import {
  detectFixtureOverlaps,
  fixturePolygon,
  polygonsOverlapMeaningful,
  segmentPolygon,
  wallPolygon,
} from './wallFootprints';
import {
  CHILD_ARRAYS,
  ENDPOINT_TOL,
  MIN_WALL_LENGTH,
  PARALLEL_DOT,
  PARALLEL_TOL,
  ZERO_DISP,
  buildJunctionGraph,
  cascadeDisplacement,
  classifyWallEndpoints,
  collectRoomPolygons,
  getXZ,
  isZeroDisp,
  pointsEqualXZ,
  sameDisp,
  wallEndpointsXZ,
  wallFromEndpoints,
} from './wallDisplacement';
import { slideJunctionsOnReangledWalls } from './junctionSlide';
import { boundaryForGrabbedEnd } from './wallLengthDrag';

export {
  MIN_WALL_LENGTH,
  PARALLEL_DOT,
  PARALLEL_TOL,
  wallFromEndpoints,
} from './wallDisplacement';

export const LOCK_EPSILON = 0.005; // 5 mm — lock-conflict tolerance
export const FIXTURE_TIGHT_MARGIN = 0.1; // 10 cm — within this of the fixture, raise a "tight fit" warning

/**
 * Classify what to do with a fixture whose footprint straddles the stretch
 * line. Stretching makes more room — the fixture can stay put, no issue.
 * Shrinking compresses the moving side toward the static side: if the
 * compression amount exceeds the fixture's static-side overhang, a wall
 * has translated past the fixture; if it's within FIXTURE_TIGHT_MARGIN
 * of doing so, raise a non-blocking warning.
 *
 * Returns one of: 'leave-alone' | 'warning' | 'error'.
 */
export function classifyStraddlingFixture(fp, delta, stretchPosition) {
  if (delta >= 0) return 'leave-alone';
  const absDelta = -delta;
  // Use the smaller of the two overhangs as the conservative threshold:
  // a wall at the stretch line moves through the static-side overhang
  // when shrinking; we don't generally know moving-side wall positions, so
  // pick the more pessimistic side.
  const overhangStatic = stretchPosition - fp.min;
  const overhangMoving = fp.max - stretchPosition;
  const minOverhang = Math.min(overhangStatic, overhangMoving);
  if (absDelta > minOverhang) return 'error';
  if (absDelta > minOverhang - FIXTURE_TIGHT_MARGIN) return 'warning';
  return 'leave-alone';
}
/**
 * 2D line-segment intersection in the XZ plane. Returns the intersection
 * point if the two finite segments cross, or null.
 */
function segmentIntersectionXZ(p1, p2, p3, p4) {
  const dx1 = p2.x - p1.x;
  const dz1 = p2.z - p1.z;
  const dx2 = p4.x - p3.x;
  const dz2 = p4.z - p3.z;
  const det = dx1 * dz2 - dz1 * dx2;
  if (Math.abs(det) < 1e-9) return null;
  const t = ((p3.x - p1.x) * dz2 - (p3.z - p1.z) * dx2) / det;
  const s = ((p3.x - p1.x) * dz1 - (p3.z - p1.z) * dx1) / det;
  const EPS = 1e-3;
  if (t < -EPS || t > 1 + EPS || s < -EPS || s > 1 + EPS) return null;
  return { x: p1.x + t * dx1, z: p1.z + t * dz1, t, s };
}

/**
 * The structure wall a measurement is OF. A measurement normally names a room
 * wall; a wall no room wall points at — one drawn outside every room, or one
 * whose length is read off the structure wall itself — is named by its own
 * id. Missing that second case is what sent a new wall's resize to the
 * whole-plan box, moving everything but the wall (call item #15).
 */
function findStructureWallByRoomWallId(structureData, roomWallId) {
  for (const room of structureData?.rooms || []) {
    for (const roomWall of room.walls || []) {
      if (!roomWall || roomWall.id !== roomWallId) continue;
      const swId = roomWall.alignment?.structureWallId;
      if (!swId) continue;
      return (
        (structureData.walls || []).find((w) => w && w.id === swId) || null
      );
    }
  }
  return (
    (structureData?.walls || []).find((w) => w && w.id === roomWallId) || null
  );
}

/**
 * A length edit runs along x or z, so it can run along a wall only within the
 * parallel tolerance of one of them. ONE rule for the measurement
 * (`getMeasurementAxis`) and the wall (`isWallLengthEditable`), which the
 * panel and the Resize button ask up front — they used to offer the edit and
 * refuse it only after a value was typed or a handle grabbed.
 */
const isAlongAnAxis = (ux, uz) =>
  Math.max(Math.abs(ux), Math.abs(uz)) >= PARALLEL_DOT;

/** What the user is told when a wall's length cannot be edited. */
export const NON_AXIAL_WALL_MESSAGE =
  "This wall isn't axis-aligned, so its length can't be edited yet.";

/** True when a length edit can run along `wall` (it is square to x or z). */
export function isWallLengthEditable(wall) {
  const [ux, uz] = apply3DRotationTo2D(wall?.rotation);
  return isAlongAnAxis(ux, uz);
}

/**
 * Why a length edit cannot run along this measurement — `'non-axial'` or
 * `'degenerate'` — or null when it can.
 */
export function wallLengthEditRefusal(measurement) {
  return getMeasurementAxis(measurement).error || null;
}

/**
 * Validate the measurement is axis-aligned and pick anchor/mover.
 * Anchor = endpoint with smaller projection along the dominant axis;
 * mover = the other endpoint.
 */
export function getMeasurementAxis(measurement) {
  if (!measurement || !measurement.start || !measurement.end) {
    return { error: 'degenerate' };
  }
  const a = { x: measurement.start[0], z: measurement.start[1] };
  const b = { x: measurement.end[0], z: measurement.end[1] };
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const length = Math.hypot(dx, dz);
  if (length < 1e-6) return { error: 'degenerate' };
  const rawDir = { x: dx / length, z: dz / length };

  // Axis-alignment check: dominant component must be ≥ cos(tol)
  const xMag = Math.abs(rawDir.x);
  const zMag = Math.abs(rawDir.z);
  const dominant = xMag >= zMag ? 'x' : 'z';
  if (!isAlongAnAxis(rawDir.x, rawDir.z)) return { error: 'non-axial' };

  // Anchor = endpoint with smaller coord on the unsigned dominant axis
  // ("more top/left"). dir always points anchor → mover, so dir is +X or +Z.
  const aDom = dominant === 'x' ? a.x : a.z;
  const bDom = dominant === 'x' ? b.x : b.z;
  const [anchor, mover] = aDom <= bDom ? [a, b] : [b, a];
  const dir = dominant === 'x' ? { x: 1, z: 0 } : { x: 0, z: 1 };
  return { dir, anchor, mover };
}

function projectAlong(point, dir) {
  return point.x * dir.x + point.z * dir.z;
}

function classifyPoint(proj, stretchPosition) {
  if (proj > stretchPosition + PARALLEL_TOL) return 'MOVING';
  if (proj < stretchPosition - PARALLEL_TOL) return 'STATIC';
  return 'BOUNDARY';
}

/**
 * Project an item's footprint extent onto `dir`. Returns
 * { min, max, center } projection range, or null if the item has no
 * positional data.
 */
function itemFootprintAlong(item, dir) {
  if (!item || !item.center) return null;
  const cProj = item.center.x * dir.x + item.center.z * dir.z;
  const halfX = (item.dimensions?.x || 0) / 2;
  const halfZ = (item.dimensions?.z || 0) / 2;
  let footprint;
  if (item.rotation) {
    const rotDir = apply3DRotationTo2D(item.rotation);
    const alongX = rotDir[0] * dir.x + rotDir[1] * dir.z;
    const alongZ = -rotDir[1] * dir.x + rotDir[0] * dir.z;
    footprint = Math.abs(alongX) * halfX + Math.abs(alongZ) * halfZ;
  } else {
    footprint = Math.max(halfX, halfZ);
  }
  return { min: cProj - footprint, max: cProj + footprint, center: cProj };
}

// getMeasurementKey is the single shared key fn (measurementKeys.js), imported
// above for internal use and re-exported so existing importers (useStretchPlan)
// keep working.
export { getMeasurementKey };

/**
 * Recompute the current distance for a lock key from the structure.
 * Returns null if the underlying wall/fixtures no longer exist.
 */
export function lookupLockedDistance(structureData, key) {
  if (!key || !structureData) return null;
  if (key.startsWith('wall:')) {
    const wallId = key.slice('wall:'.length);
    for (const room of structureData.rooms || []) {
      for (const wall of room.walls || []) {
        if (wall && wall.id === wallId) return wall.dimensions?.x ?? null;
      }
    }
    return null;
  }
  return null; // v2 partial — not supported here
}

/**
 * Compute a StretchPlan for the requested edit.
 *
 * Algorithm:
 *  1. Validate axis from measurement (axis-aligned, non-degenerate).
 *  2. Classify structure walls based on endpoint projection along `dir`
 *     vs `stretchPosition`. Each wall becomes 'translate' (both endpoints
 *     moving) or 'stretch' (one moving, one static). A non-parallel
 *     'stretch' wall still applies (its moving end follows the junction,
 *     re-angling the wall) but raises a non-blocking 'tilted-wall' warning.
 *  3. Cascade to room walls via `rewrapRoomWall`. Position children with
 *     the per-wall-kind rule (see module docstring).
 *  4. Reposition polygon corners by per-edge classification.
 *  5. Translate free-standing objects by `delta * dir` if their center
 *     projection is on the moving side.
 *  6. Run lock check by applying the in-progress plan to a clone, then
 *     comparing every existing locked measurement's distance against its
 *     stored value. Drop orphan locks; flag mismatches as conflicts.
 */
/**
 * @param {Object} structureData - Structure with walls, rooms, etc.
 * @param {Object} measurement - The measurement object the user clicked.
 * @param {number} newValueMeters - User-typed new length.
 * @param {Object} [opts]
 * @param {number} [opts.boundary] - Projection along `dir` of the static/
 *   moving divider. Junctions with `proj > boundary` are moving;
 *   `proj <= boundary` are static. Defaults to the edited wall's anchor
 *   projection (so the whole structure past the anchor follows the
 *   stretch — the typical "extend this wall, push everything east"
 *   behavior). Pass an explicit value to lock the divider elsewhere.
 * @param {{ min: number, max: number }} [opts.extent] - Optional bounds along
 *   the axis perpendicular to `dir` that confine the moving region to a finite
 *   band (projection in `[min, max]`). A point moves only if it's past the
 *   divider AND within the band. Omit (or pass non-finite bounds) for the
 *   default infinite-line divider that sweeps the whole structure. Used to
 *   limit a stretch to one part of the plan (e.g. one column of rooms) so the
 *   rest is left untouched.
 * @param {{x: number, z: number}} [opts.grabbedPoint] - The end handle the
 *   user dragged, when the edit came from a drag. Decides which end moves on
 *   a wall whose two ends are both free.
 */
/**
 * The endpoint of `wall` that meets nothing — no other wall (tight endpoint
 * match or loose T-junction) and no room floor-polygon corner. Returns null
 * when both ends are anchored, which is the common case; when BOTH are free
 * (a floating partition) the end the user grabbed wins (`preferPoint`), else
 * the one nearest the measurement's mover end, so a typed edit still grows in
 * the direction the measurement is read.
 */
function findFreeWallEnd(structureData, wall, axis, preferPoint = null) {
  if (!wall) return null;
  const ends = classifyWallEndpoints(
    wall,
    structureData?.walls,
    collectRoomPolygons(structureData)
  );
  if (ends.aConnected && ends.bConnected) return null;
  let free;
  if (ends.aConnected) free = ends.b;
  else if (ends.bConnected) free = ends.a;
  else {
    const toward = preferPoint ?? axis.mover;
    const distTo = (p) => Math.abs(p.x - toward.x) + Math.abs(p.z - toward.z);
    free = distTo(ends.a) < distTo(ends.b) ? ends.a : ends.b;
  }
  // Only the wall whose MEASUREMENT reaches that end is "the free-ended wall"
  // being edited. A long structure wall carries several room walls, and one
  // partway along it is a perfectly ordinary connected wall — the free end is
  // metres away and is not what the user is dragging. Boxing it in anyway
  // stretches a sliver across everything in between, and every wall crossing
  // the sliver's side edges reports `region-slant` (blocking) — measured on 3
  // of the 15 client-edit corpus scans.
  return pointsEqualXZ(free, axis.anchor) || pointsEqualXZ(free, axis.mover)
    ? free
    : null;
}

/**
 * The default translate-rectangle for a wall whose far end meets nothing, or
 * null when both ends are anchored (the caller then uses the whole-structure
 * default). Shared by `computeStretch` and `useStretchPlan` so the box the
 * canvas draws is the box the plan is computed against.
 *
 * `end` is the free endpoint the box is built around — the one that moves.
 * `preferPoint` is the end the user grabbed, which decides between two free
 * ends.
 */
export function freeEndRegionDefault(
  structureData,
  measurement,
  axis,
  { preferPoint = null } = {}
) {
  const wall = findStructureWallByRoomWallId(
    structureData,
    measurement?.wallId
  );
  const end = findFreeWallEnd(structureData, wall, axis, preferPoint);
  if (!end) return null;
  const dir = axis.dir;
  const perp = { x: -dir.z, z: dir.x };
  const perpProjOf = (pos) => pos.x * perp.x + pos.z * perp.z;
  return {
    ...regionAroundWallEnd(wall, axis, end, { dir, perpProjOf }),
    end,
  };
}

/**
 * A translate-rectangle that covers the edited wall and nothing else: cut
 * just inside `end` (so the one-sided rule puts exactly that end in motion),
 * closed just past it along `dir`, and only as wide as the wall's own
 * footprint plus the tolerance that decides what counts as joined to it.
 *
 * The near/far edges come from `boundaryForGrabbedEnd` — the same placement
 * an end-handle drag uses — measured against the MEASUREMENT's own ends, which
 * are what the one-sided rule tests (a room wall is often inset from its
 * structure wall's endpoints by a miter).
 */
function regionAroundWallEnd(wall, axis, end, { dir, perpProjOf }) {
  const endProj = end.x * dir.x + end.z * dir.z;
  const { boundary, farBoundary } = boundaryForGrabbedEnd(
    axis,
    end,
    { minD: endProj, maxD: endProj },
    ENDPOINT_TOL
  );
  const { a, b } = wallEndpointsXZ(wall);
  const halfThickness = (wall.dimensions?.z || 0) / 2;
  const pa = perpProjOf(a);
  const pb = perpProjOf(b);
  const pad = halfThickness + ENDPOINT_TOL;
  return {
    boundary,
    farBoundary,
    extent: { min: Math.min(pa, pb) - pad, max: Math.max(pa, pb) + pad },
  };
}

/**
 * Expansion collision check for a wall end that meets nothing. The added
 * segment (`from` → `to`) must not run through another wall or a
 * free-standing fixture. Wall-mounted children (doors, windows, openings)
 * are NOT treated as separate fixtures — if the wall runs through them it is
 * the underlying wall that gets reported.
 *
 * Only walls and fixtures that STAY PUT can be hit: anything the same edit
 * moves keeps its distance from the growing wall.
 */
function detectFreeEndExpansionCollisions(
  structureData,
  { wall, from, to, isMovingWallId, isMovingItem }
) {
  const errors = [];
  // The real footprints, not their boxes: a slanted wall's box covers a
  // triangle of empty floor either side of it, and a stub growing through
  // that empty floor read as crashing into the wall (Testing / Scan 1: a
  // 1⅜" extension refused, the slanted wall 40 cm away).
  const segPoly = segmentPolygon(from, to, wall.dimensions?.z || 0.1);
  if (!segPoly) return errors;

  let wallHit = null;
  for (const other of structureData.walls || []) {
    if (
      !other ||
      other.id === wall.id ||
      !other.center ||
      !other.dimensions ||
      isMovingWallId(other.id)
    ) {
      continue;
    }
    const otherPoly = wallPolygon(other);
    if (!otherPoly) continue;
    if (!polygonsOverlapMeaningful(segPoly, otherPoly)) continue;
    // Skip if the only overlap is at the wall's existing end (by
    // construction that end is free, but ENDPOINT_TOL vs wall thickness can
    // still produce a borderline overlap there).
    const otherEnds = wallEndpointsXZ(other);
    if (pointsEqualXZ(otherEnds.a, from) || pointsEqualXZ(otherEnds.b, from)) {
      continue;
    }
    wallHit = other;
    break;
  }
  if (wallHit) {
    return [
      {
        code: 'expansion-collision',
        ref: { wallId: wall.id, otherWallId: wallHit.id },
      },
    ];
  }

  const objectLists = [];
  if (Array.isArray(structureData.objects)) {
    objectLists.push({ items: structureData.objects, roomId: null });
  }
  for (const room of structureData.rooms || []) {
    if (Array.isArray(room.objects)) {
      objectLists.push({ items: room.objects, roomId: room.id });
    }
  }
  for (const { items, roomId } of objectLists) {
    for (const fix of items) {
      if (
        !fix ||
        !fix.center ||
        fix.parentWallIds?.[0] === wall.id ||
        isMovingItem(fix)
      ) {
        continue;
      }
      const fixPoly = fixturePolygon(fix);
      if (!fixPoly) continue;
      if (!polygonsOverlapMeaningful(segPoly, fixPoly)) continue;
      errors.push({
        code: 'expansion-fixture-collision',
        ref: { wallId: wall.id, fixtureId: fix.id, roomId },
      });
      return errors;
    }
  }
  return errors;
}

/**
 * The box `computeStretch` uses when handed none: the editor's default
 * (`defaultStretchRegion`, its cut kept out of every floor the edit can
 * reach and clear of the edited floor's locked walls where it can be), its
 * cut then re-settled out of any wall this delta would collapse. Null when
 * the plan has nothing to place it against.
 */
function placeDefaultRegion(structureData, measurement, axis, delta, opts) {
  const scoped = scopeStructureForMeasurement(
    structureData,
    measurement,
    opts.story ?? null
  );
  const reachable = reachableStructureForMeasurement(
    structureData,
    measurement,
    opts.story ?? null
  );
  const axisInfo = {
    ...axis,
    anchorProj: projectAlong(axis.anchor, axis.dir),
    moverProj: projectAlong(axis.mover, axis.dir),
  };
  // Sized by the whole structure: the edit may recruit another floor.
  const bounds = stretchRegionBounds(structureData, axis.dir);
  // Placed against every floor the edit can reach: it may recruit another.
  const region = defaultStretchRegion(reachable, axisInfo, {
    bounds,
    grabbedPoint: opts.grabbedPoint || null,
    lockRanges: computeLockedBoundaryZones(scoped, measurement).map(
      (z) => z.range
    ),
  });
  if (!region) return null;
  const collapse = computeBoundaryClampZones(scoped, measurement, delta).map(
    (z) => z.range
  );
  if (!collapse.length) return region;
  return resnapCut(region, axisInfo, bounds, [
    ...structuralCutRanges(reachable, axis.dir),
    ...collapse,
  ]).region;
}

export function computeStretch(
  structureData,
  measurement,
  newValueMeters,
  opts = {}
) {
  const lockUpdates = { add: [], remove: [] };
  const errors = [];
  const editedKey = getMeasurementKey(measurement);

  const axis = getMeasurementAxis(measurement);
  if (axis.error) {
    return {
      delta: 0,
      direction: { x: 0, z: 0 },
      stretchPosition: 0,
      affectedStructureWalls: [],
      derivedRoomWalls: [],
      affectedItems: [],
      affectedPolygonCorners: [],
      affectedStructureFloorCorners: [],
      errors: [{ code: axis.error }],
      lockUpdates,
    };
  }

  const dir = axis.dir;
  const stretchPosition = projectAlong(axis.mover, dir);
  const anchorProjection = projectAlong(axis.anchor, dir);
  // `let` because a box covering the edited wall's anchor side (rather than
  // its mover side) flips the sign — see the one-sided-region check below.
  let delta = newValueMeters - measurement.distance;

  // The bounded stretch region — the translate-rectangle. In `(dir, perp)`
  // space it is the box `[boundary, farBoundary] × [extent.min, extent.max]`:
  // everything inside translates by `delta * dir`, everything outside stays.
  //
  // Default boundary: midpoint of the edited wall along `dir`. Each half of
  // the structure stays put relative to the wall on its own side — the
  // static-side half doesn't move, the moving-side half shifts by delta. The
  // user drags the slider to shift the cut. Junction classification is
  // `proj >= boundary` ⇒ moving (the boundary line itself is inclusive on the
  // moving side). With no `extent` the band is the whole plane and no finite
  // `farBoundary` the region is open-ended — identical to the original
  // infinite half-plane.
  //
  // These are `let` because a wall with a FREE END defaults to a box hugging
  // the wall itself, and that can only be resolved after the storey scope has
  // narrowed the structure (see below). Every reader is a closure over these
  // bindings and runs after that point.
  const perp = { x: -dir.z, z: dir.x };
  const perpProjOf = (pos) => pos.x * perp.x + pos.z * perp.z;
  const callerGaveRegion =
    opts.boundary != null ||
    Number.isFinite(opts.farBoundary) ||
    (opts.extent &&
      Number.isFinite(opts.extent.min) &&
      Number.isFinite(opts.extent.max));
  // Handed no box, place the one the floor plan's editor would
  // (`defaultStretchRegion`, one rule for both) — so an edit measured here is
  // the edit the app makes — then re-settle its cut out of any wall this
  // value would collapse, as the editor does when a value is typed.
  const defaultRegion = callerGaveRegion
    ? null
    : placeDefaultRegion(structureData, measurement, axis, delta, opts);
  let boundary =
    opts.boundary != null
      ? opts.boundary
      : (defaultRegion?.boundary ?? (anchorProjection + stretchPosition) / 2);
  let extent =
    opts.extent &&
    Number.isFinite(opts.extent.min) &&
    Number.isFinite(opts.extent.max)
      ? opts.extent
      : (defaultRegion?.extent ?? null);
  let farBoundary = Number.isFinite(opts.farBoundary)
    ? opts.farBoundary
    : (defaultRegion?.farBoundary ?? Infinity);
  const withinDirBand = (proj) =>
    proj >= boundary - PARALLEL_TOL && proj <= farBoundary + PARALLEL_TOL;
  const withinExtent = (pos) => {
    if (!extent || !pos) return true;
    const pp = perpProjOf(pos);
    return pp >= extent.min - PARALLEL_TOL && pp <= extent.max + PARALLEL_TOL;
  };

  // STOREY SCOPE — see utils/stretchStoryScope.js. The edit runs over the
  // edited wall's own floor plus any floor a moving SHARED wall recruits. From
  // here on `structureData` is that narrowed view; `fullStructureData` stays
  // for the lock check, whose re-measure must still see the walls the scope
  // left out (a wall it can't find reads as a lock to remove).
  const fullStructureData = structureData;
  const isWallMovingForScope = (wall) => {
    if (!wall?.center || !wall?.dimensions) return false;
    const ends = wallEndpointsXZ(wall);
    return [ends.a, ends.b].some(
      (p) => withinDirBand(projectAlong(p, dir)) && withinExtent(p)
    );
  };
  const scopeInfo = resolveStretchStoryScope(structureData, {
    story: opts.story,
    editedRoomWallId: measurement.wallId,
    isWallMoving: isWallMovingForScope,
  });
  structureData = scopeInfo.structureData;

  const affectedStructureWalls = [];

  // FREE-ENDED WALL — the default translate-region hugs the wall itself.
  //
  // A wall with an endpoint that meets nothing (no other wall, no room
  // floor-polygon corner) is a half-wall / dangling partition / floating
  // wall: pushing its free end out has no reason to drag the rest of the
  // plan with it. That used to be a SEPARATE geometry path that skipped the
  // junction graph and ignored the translate-rectangle entirely — so the
  // rectangle was drawn, and dragged, over an edit it could not affect.
  //
  // Now it is only a DEFAULT: the box is seeded around the free end so that
  // by default nothing but this wall moves (the previous behaviour), and the
  // edit runs through the same cascade as every other one, so widening the
  // box does what the box says it does. Applied only when the caller supplies
  // no region at all: the box the hook seeds always wins.
  const editedStructureWall = findStructureWallByRoomWallId(
    structureData,
    measurement.wallId
  );
  const freeEndRegion = freeEndRegionDefault(structureData, measurement, axis, {
    preferPoint: opts.grabbedPoint ?? null,
  });
  const freeEnd = freeEndRegion ? freeEndRegion.end : null;
  if (freeEndRegion && !callerGaveRegion) {
    boundary = freeEndRegion.boundary;
    farBoundary = freeEndRegion.farBoundary;
    extent = freeEndRegion.extent;
  }

  // One-sided-region check. With the rectangle the box
  // can be placed over EITHER side of the edited wall. Exactly one of its two
  // ends must sit inside the box — that end moves, the other stays, and the
  // wall reaches the typed length. If the box covers BOTH ends (the wall would
  // just translate, no length change) or NEITHER (nothing happens), the edit
  // is ill-posed → blocking `region-not-one-sided`. When the box covers the
  // ANCHOR side (mover end static), flip the move sign so the anchor end moves
  // *away* from the static mover and the wall still changes by `delta` — every
  // downstream `delta * dir` shift then points the right way. (No sign-test on
  // `delta` runs past this point in the propagated path, so the flip is safe.)
  if (editedStructureWall) {
    const anchorIn =
      withinDirBand(anchorProjection) && withinExtent(axis.anchor);
    const moverIn = withinDirBand(stretchPosition) && withinExtent(axis.mover);
    if (anchorIn === moverIn) {
      errors.push({ code: 'region-not-one-sided' });
    } else if (!moverIn) {
      delta = -delta;
    }
  }

  // 1. Classify via the junction graph.
  //
  // `buildJunctionGraph` clusters every connection point (wall endpoints +
  // polygon corners) within ENDPOINT_TOL into ONE junction. Each junction
  // gets ONE moving/static label here, so two walls meeting at the same
  // corner can never disagree about whether they follow the stretch —
  // L-junctions stay intact even when scan-data jitter pushes endpoints a
  // few cm apart.
  //
  // The static/moving cut is *strict*: `proj >= boundary` ⇒ moving,
  // `proj < boundary` ⇒ static. No tolerance band — that's what `boundary`
  // is for (see the draggable-line UI). The mover endpoint of the edited
  // wall sits on the boundary by default and qualifies as moving.
  //
  // A junction is classified by the LARGEST projection among its members,
  // not by its representative: two identically-placed endpoints whose reps
  // land a few mm apart would otherwise classify differently.
  const graph = buildJunctionGraph(structureData);
  const junctionMoving = graph.junctions.map((j) => {
    let maxProj = -Infinity;
    for (const m of j.members) {
      const proj = m.x * dir.x + m.z * dir.z;
      if (proj > maxProj) maxProj = proj;
    }
    return withinDirBand(maxProj) && withinExtent(j.pos);
  });

  const junctionMovingFor = (point) => {
    const i = graph.indexFor(point);
    if (i >= 0) return junctionMoving[i];
    // Fallback (shouldn't happen for walls/corners — they're in the graph).
    const proj = point.x * dir.x + point.z * dir.z;
    return withinDirBand(proj) && withinExtent(point);
  };

  // The displacement field this edit imposes: `delta * dir` on the moving
  // side, nothing on the static side — except a junction partway along a
  // wall the edit re-angles, which slides with that wall by a share of the
  // change (utils/junctionSlide). `cascadeDisplacement` reads only this.
  // The edited wall's own ends keep the box's verdict: the length the user
  // asked for is the distance between them.
  const movingDisp = { x: delta * dir.x, z: delta * dir.z };
  const isAlongDir = (wall) => {
    const wd = apply3DRotationTo2D(wall.rotation);
    return Math.abs(wd[0] * dir.x + wd[1] * dir.z) > PARALLEL_DOT;
  };
  const boxDisp = junctionMoving.map((m) => (m ? movingDisp : ZERO_DISP));
  const editedEnds = editedStructureWall
    ? wallEndpointsXZ(editedStructureWall)
    : null;
  const slides = slideJunctionsOnReangledWalls(structureData, graph, {
    boxDisp,
    isReangled: (wall) => !isAlongDir(wall),
    fixedJunctions: new Set(
      editedEnds
        ? [graph.indexFor(editedEnds.a), graph.indexFor(editedEnds.b)]
        : []
    ),
  });
  const junctionDispAt = boxDisp.map((d, i) => slides.get(i) || d);
  // The slide of the junction `p` belongs to, or null. Checked against the
  // few sliding junctions' own points before the full lookup, which runs for
  // every room-wall point on every frame of a drag.
  const slidMembers = [...slides.keys()].flatMap((k) =>
    graph.junctions[k].members.map((m) => ({ m, k }))
  );
  const slideAt = (p) => {
    if (!slidMembers.length || !p) return null;
    const hit = slidMembers.some(
      ({ m }) =>
        Math.abs(m.x - p.x) < ENDPOINT_TOL && Math.abs(m.z - p.z) < ENDPOINT_TOL
    );
    return hit ? slides.get(graph.indexFor(p)) || null : null;
  };
  // True when the two ends of a line moved apart only ALONG it: it keeps its
  // direction (a stretch along itself), so it is not re-angled.
  const movesAlong = (line, dA, dB) => {
    const ld = apply3DRotationTo2D(line.rotation);
    const rx = dB.x - dA.x;
    const rz = dB.z - dA.z;
    return Math.abs(rx * ld[1] - rz * ld[0]) < 1e-9;
  };
  const junctionDisp = (point) => {
    const i = graph.indexFor(point);
    if (i >= 0) return junctionDispAt[i];
    return junctionMovingFor(point) ? movingDisp : ZERO_DISP;
  };
  // Children and free-standing objects classify by their own centre against
  // the translate-rectangle, not by a junction — a door in the middle of a
  // wall belongs to no junction at all.
  const regionDisp = (center) =>
    center &&
    withinDirBand(center.x * dir.x + center.z * dir.z) &&
    withinExtent(center)
      ? movingDisp
      : ZERO_DISP;

  for (const wall of structureData.walls || []) {
    if (!wall || !wall.center || !wall.dimensions) continue;
    const { a, b, length: oldLen } = wallEndpointsXZ(wall);
    const dA = junctionDisp(a);
    const dB = junctionDisp(b);

    if (isZeroDisp(dA) && isZeroDisp(dB)) continue; // static — skip

    if (sameDisp(dA, dB)) {
      const newA = { x: a.x + dA.x, z: a.z + dA.z };
      const newB = { x: b.x + dB.x, z: b.z + dB.z };
      affectedStructureWalls.push({
        wallId: wall.id,
        kind: 'translate',
        before: wall,
        after: wallFromEndpoints(wall, newA, newB),
      });
      continue;
    }

    // Mixed: its ends move by different amounts (one with the box and one
    // not, or one sliding along a re-angled wall) → stretch. A wall that
    // isn't parallel to the stretch axis can't preserve its angle through
    // the move — each end follows its junction along `dir`, so the wall ends
    // up re-angled (and re-lengthed).
    const wallParallel = isAlongDir(wall);
    // A junction sliding along a re-angled wall can move a partition's end
    // along the partition itself: it changes length and keeps its angle.
    const turns = !wallParallel && !movesAlong(wall, dA, dB);
    if (turns) {
      // A non-parallel wall cut by a *lateral* edge of the region (one
      // endpoint inside the extent band, one outside) would be slanted by the
      // box itself → reject the region (blocking `region-slant`). A
      // non-parallel wall fully inside the band that merely straddles the
      // divider is the freeform diagonal case → non-blocking `tilted-wall`
      // warning: the wall re-angles but the edit proceeds (junction stays
      // connected via the same moving-endpoint shift as the parallel case).
      const straddlesLateral = extent && withinExtent(a) !== withinExtent(b);
      errors.push(
        straddlesLateral
          ? { code: 'region-slant', ref: { wallId: wall.id } }
          : {
              code: 'tilted-wall',
              severity: 'warning',
              ref: { wallId: wall.id },
            }
      );
    }
    const movedA = { x: a.x + dA.x, z: a.z + dA.z };
    const movedB = { x: b.x + dB.x, z: b.z + dB.z };
    const after = wallFromEndpoints(wall, movedA, movedB);
    affectedStructureWalls.push({
      wallId: wall.id,
      kind: 'stretch',
      before: wall,
      after,
      // Its children ride it rather than moving by the box: see
      // `rideReangledWall`. A wall within the parallel tolerance only
      // stretches along itself and keeps the box rule.
      ...(turns ? { reangled: true } : {}),
    });
    const newLen = after.dimensions.x;
    if (newLen < MIN_WALL_LENGTH) {
      errors.push({
        code: 'wall-would-collapse',
        ref: { wallId: wall.id, currentLength: oldLen, wouldBecome: newLen },
      });
    }
  }

  // 1.5. A free end pushed OUT can run into something that isn't moving.
  //      Kept from the old independent path: it is the one check that
  //      belongs to a wall end with nothing on it, and the rest of the plan
  //      has no cascade to resolve the overlap.
  if (freeEnd && editedStructureWall) {
    const entry = affectedStructureWalls.find(
      (e) => e.wallId === editedStructureWall.id
    );
    const grew =
      entry && entry.after.dimensions.x > entry.before.dimensions.x + 1e-9;
    if (grew) {
      const oldEnds = wallEndpointsXZ(entry.before);
      const newEnds = wallEndpointsXZ(entry.after);
      const movedA =
        Math.hypot(newEnds.a.x - oldEnds.a.x, newEnds.a.z - oldEnds.a.z) >=
        Math.hypot(newEnds.b.x - oldEnds.b.x, newEnds.b.z - oldEnds.b.z);
      const from = movedA ? oldEnds.a : oldEnds.b;
      const to = movedA ? newEnds.a : newEnds.b;
      if (pointsEqualXZ(from, freeEnd)) {
        const movingWallIds = new Set(
          affectedStructureWalls.map((e) => e.wallId)
        );
        errors.push(
          ...detectFreeEndExpansionCollisions(structureData, {
            wall: editedStructureWall,
            from,
            to,
            isMovingWallId: (id) => movingWallIds.has(id),
            isMovingItem: (fix) => regionDisp(fix.center) !== ZERO_DISP,
          })
        );
      }
    }
  }

  // 2. Cascade — one shared engine (utils/wallDisplacement.js) turns the
  //    displacement field into room walls, exterior walls, children,
  //    polygon corners, structure-floor corners and the room
  //    translate/reshape split. The wall drag feeds it a different field
  //    and gets the identical cascade, so a fix here lands on both.
  //
  //    Room walls (and exterior walls, same helper) snap via *per-endpoint
  //    offsets*, cut at the stretch boundary rather than the structure
  //    wall's geometric midpoint: a long structure wall shared across two
  //    rooms has a moving junction in its middle, so its own midpoint falls
  //    past the partition for one of the room walls and would anchor that
  //    wall's interior endpoint to the wrong side.
  //
  //    "Moving" is membership of the BOX, the same test the junctions,
  //    children and corners use. It used to be the half-plane past the near
  //    edge alone, which is the box only while it covers the mover side and
  //    runs to the plan's far end: with the box over the ANCHOR end (an end
  //    handle drag on that end builds exactly that) every point past the
  //    near edge read as moving, and each room wall on a stretched wall slid
  //    by the whole change, the ones outside the box included.
  const movingSide = (q) =>
    withinDirBand(q.x * dir.x + q.z * dir.z) && withinExtent(q);
  const stretchPickOffset = (p, { offsetA, offsetB, oldEnds }) => {
    // A room wall's end at a sliding T goes where the T goes, the floor
    // corner beside it too (utils/junctionSlide).
    const slid = slideAt(p);
    if (slid) return slid;
    // The host's own end follows its junction, as the host itself did.
    const aMoving = junctionMovingFor(oldEnds.a);
    const movingOffset = aMoving ? offsetA : offsetB;
    const staticOffset = aMoving ? offsetB : offsetA;
    // A room wall is a sub-segment of its host's line, drawn half a wall
    // thickness inboard of the centreline. Classify the point where it MEETS
    // the host, so it can never come out on a different side of the boundary
    // from the host geometry it rides on — on a host that doesn't run along
    // `dir`, that inset leaks straight into the projection.
    const u = oldEnds.dir;
    const len = oldEnds.length || 0;
    const along =
      len > 1e-9 ? (p.x - oldEnds.a.x) * u.x + (p.z - oldEnds.a.z) * u.z : 0;
    const s = Math.min(len, Math.max(0, along));
    const hostParallel = Math.abs(u.x * dir.x + u.z * dir.z) > PARALLEL_DOT;
    if (!hostParallel) {
      // The host does not run along `dir`, so the boundary cannot separate
      // its two ends cleanly and the plan is re-angling it (`tilted-wall`).
      // Carry the point by the host's OWN map — the affine image, exact for
      // a re-angle and for a rigid translate alike.
      //
      // The parameter is NOT clamped to the host's span: a room wall is
      // routinely LONGER than the centreline it rides on (a mitred end
      // overhangs by about a wall thickness), and the affine image of the
      // host's LINE is what keeps the overhang collinear. Clamping hands the
      // overhanging end the host end's own offset, which on a re-angled host
      // leaves it off the new line by the overhang times the swing.
      const t = len > 1e-9 ? along / len : 0;
      return {
        x: offsetA.x + (offsetB.x - offsetA.x) * t,
        z: offsetA.z + (offsetB.z - offsetA.z) * t,
      };
    }
    const onHost = { x: oldEnds.a.x + u.x * s, z: oldEnds.a.z + u.z * s };
    // One of its ends slid along a re-angled wall (utils/junctionSlide): its
    // offsets no longer say how the rest of it moves. A point at the slid end
    // took the slide above; any other — a T partway along it, where another
    // wall meets it — moves as the box moves it, as that wall's own end does.
    // The slid end's offset, or a share of the two, left the room walls there
    // half the change off their floor corners (review on #3015).
    if (slideAt(oldEnds.a) || slideAt(oldEnds.b)) {
      return movingSide(onHost) ? movingDisp : ZERO_DISP;
    }
    return movingSide(onHost) ? movingOffset : staticOffset;
  };

  const cascade = cascadeDisplacement(structureData, {
    affectedStructureWalls,
    junctionDisp,
    // Uniform midpoint-vs-region rule for openings AND objects, matching
    // the structure-level and exterior cascades — a physical fixture
    // present at several levels stays in sync because they share `center`.
    childDisp: (child) => regionDisp(child.center),
    freeObjectDisp: (obj) => regionDisp(obj.center),
    pickOffset: stretchPickOffset,
    // A room wall on an UNAFFECTED structure wall that straddles a lateral
    // box edge would tilt off its (stationary) structure wall — reject the
    // region. A parallel wall just slides along its wall's line.
    onStraddleWall: (roomWall, dA, dB, { room }) => {
      const wd = apply3DRotationTo2D(roomWall.rotation);
      if (Math.abs(wd[0] * dir.x + wd[1] * dir.z) > PARALLEL_DOT) return null;
      // Its ends moved apart only along it — one at a junction sliding along
      // a re-angled wall: it keeps its angle on its unmoved wall.
      if (movesAlong(roomWall, dA, dB)) return null;
      errors.push({
        code: 'region-slant',
        ref: { wallId: roomWall.id, roomId: room.id },
      });
      return 'skip';
    },
    // A polygon edge crossing the divider that isn't parallel to `dir`
    // comes out at a new angle. Cut by a *lateral* box edge → blocking
    // `region-slant`; otherwise the freeform diagonal case → non-blocking
    // `tilted-wall` (the corner still shifts).
    onStraddleEdge: (room, i, ca, cb) => {
      const ex = cb.x - ca.x;
      const ez = cb.z - ca.z;
      const elen = Math.hypot(ex, ez);
      if (elen <= 1e-9) return;
      const dot = (ex * dir.x + ez * dir.z) / elen;
      if (Math.abs(dot) >= PARALLEL_DOT) return;
      // Corners moved apart only along the edge — one slid along a
      // re-angled wall's partition: the edge keeps its angle.
      if (slideAt(ca) || slideAt(cb)) {
        const dA = junctionDisp(ca);
        const dB = junctionDisp(cb);
        if (Math.abs((dB.x - dA.x) * ez - (dB.z - dA.z) * ex) < 1e-9 * elen)
          return;
      }
      const straddlesLateral = extent && withinExtent(ca) !== withinExtent(cb);
      errors.push(
        straddlesLateral
          ? { code: 'region-slant', ref: { roomId: room.id, edge: i } }
          : {
              code: 'tilted-wall',
              severity: 'warning',
              ref: { roomId: room.id, edge: i },
            }
      );
    },
    errors,
  });

  const {
    derivedRoomWalls,
    derivedExteriorWalls,
    affectedItems,
    affectedPolygonCorners,
    affectedStructureFloorCorners,
    roomClassification,
    affectedStructureWallsById,
  } = cascade;

  // 2.5. Snap the EDITED room wall to exactly the user's input length.
  //
  // `rewrapRoomWall` maps proportionally — `new_room_length =
  // old_room_length × (new_struct_length / old_struct_length)` — so when
  // the room and structure walls aren't exactly the same length (corner
  // mitering, thickness offsets, scan jitter) the post-rewrap room
  // length drifts by a few millimetres from `userInput`. The user types
  // `10' 0"` and gets back `9' 11 15/16"`. Override the edited room
  // wall here so its `dimensions.x` equals `newValueMeters` exactly,
  // anchored at whichever endpoint moved least during the rewrap. Other
  // cascaded room walls keep their proportional rewrap.
  if (measurement?.wallId) {
    const editedEntry = derivedRoomWalls.find(
      (e) => e.wallId === measurement.wallId
    );
    if (editedEntry && editedEntry.before && editedEntry.after) {
      const oldEnds = wallEndpointsXZ(editedEntry.before);
      const afterEnds = wallEndpointsXZ(editedEntry.after);
      const aMove = Math.hypot(
        afterEnds.a.x - oldEnds.a.x,
        afterEnds.a.z - oldEnds.a.z
      );
      const bMove = Math.hypot(
        afterEnds.b.x - oldEnds.b.x,
        afterEnds.b.z - oldEnds.b.z
      );
      const staticIsA = aMove <= bMove;
      const staticPt = staticIsA ? afterEnds.a : afterEnds.b;
      // Wall's direction from the static endpoint toward the mover.
      const sign = staticIsA ? 1 : -1;
      const newMover = {
        x: staticPt.x + sign * newValueMeters * afterEnds.dir.x,
        z: staticPt.z + sign * newValueMeters * afterEnds.dir.z,
      };
      // Spread the *cascaded* wall (not the pre-stretch one) so the
      // cascade's per-end-offset updates to `alignment` and
      // `polygonCorners` survive. Then fix up the moving-side corners
      // by the residual snap delta — typically sub-mm — so alignment
      // and polygon outline match the post-snap centerline exactly.
      const cascadeMover = staticIsA ? afterEnds.b : afterEnds.a;
      const dxResid = newMover.x - cascadeMover.x;
      const dzResid = newMover.z - cascadeMover.z;
      const snapped = wallFromEndpoints(
        editedEntry.after,
        staticIsA ? staticPt : newMover,
        staticIsA ? newMover : staticPt
      );
      // Static/moving classifier: project a corner along the wall's
      // direction; the cascade put moving-side corners on `cascadeMover`
      // side of the wall midpoint.
      const ux = afterEnds.dir.x;
      const uz = afterEnds.dir.z;
      const midProj =
        ((afterEnds.a.x + afterEnds.b.x) * ux +
          (afterEnds.a.z + afterEnds.b.z) * uz) /
        2;
      const isMoverSide = (p) => {
        const proj = p.x * ux + p.z * uz;
        return staticIsA ? proj > midProj : proj < midProj;
      };
      const shiftMover = (p) =>
        isMoverSide(p) ? { ...p, x: p.x + dxResid, z: p.z + dzResid } : p;

      if (snapped.alignment) {
        const a = { ...snapped.alignment };
        if (a.bottomLeft) a.bottomLeft = shiftMover(a.bottomLeft);
        if (a.bottomRight) a.bottomRight = shiftMover(a.bottomRight);
        if (a.bottomMiddle) a.bottomMiddle = shiftMover(a.bottomMiddle);
        snapped.alignment = a;
      }
      // polygonCorners are now shifted by `wallFromEndpoints` itself —
      // the per-end fold applies the same `dxResid / dzResid` to corners
      // anchored to the moving endpoint and leaves static-side corners
      // alone, matching what the prior explicit pass did.
      editedEntry.after = snapped;
    }
  }

  // 5. Mark the edited key for auto-lock
  if (editedKey) lockUpdates.add.push(editedKey);

  // 5.5. Post-translation overlap detection (non-blocking) — shared with
  //      the wall drag, see utils/wallFootprints.js.
  errors.push(
    ...detectFixtureOverlaps(structureData, {
      affectedItems,
      affectedStructureWallsById,
    })
  );

  // 6. Lock check — applyStretchPlan is pure substitution and never
  //    re-enters computeStretch, so this is a single-level evaluation.
  if (
    fullStructureData.measurementLocks &&
    Object.keys(fullStructureData.measurementLocks).length > 0
  ) {
    const partialPlan = {
      delta,
      direction: dir,
      stretchPosition,
      boundary,
      affectedStructureWalls,
      derivedRoomWalls,
      derivedExteriorWalls,
      affectedItems,
      affectedPolygonCorners,
      affectedStructureFloorCorners,
      roomClassification,
      errors: [],
      lockUpdates: { add: [], remove: [] },
    };
    const cloned = applyStretchPlan(fullStructureData, partialPlan);
    for (const [key, lockedValue] of Object.entries(
      fullStructureData.measurementLocks
    )) {
      if (key === editedKey) continue;
      // Opening locks (opening-width:/opening-pos:) are owned by the opening
      // editor, which shares this measurementLocks store. Stretch must ignore
      // them — neither conflict-check nor (via lookupLockedDistance === null)
      // remove them.
      if (!key.startsWith('wall:') && !key.startsWith('partial:')) continue;
      const recomputed = lookupLockedDistance(cloned, key);
      if (recomputed === null) {
        lockUpdates.remove.push(key);
        continue;
      }
      if (Math.abs(recomputed - lockedValue) > LOCK_EPSILON) {
        errors.push({
          code: 'locked-conflict',
          lockKey: key,
          lockedValue,
          wouldBecome: recomputed,
        });
      }
    }
  }

  const storyScope = scopeInfo.scope
    ? {
        ...scopeInfo.scope,
        byStory: countStoryScope(scopeInfo.scope, scopeInfo.lookup, {
          affectedStructureWalls,
          roomClassification,
          rooms: structureData.rooms,
        }),
      }
    : null;

  return {
    delta,
    direction: dir,
    stretchPosition,
    boundary,
    farBoundary,
    extent,
    storyScope,
    affectedStructureWalls,
    derivedRoomWalls,
    derivedExteriorWalls,
    affectedItems,
    affectedPolygonCorners,
    affectedStructureFloorCorners,
    roomClassification,
    errors,
    lockUpdates,
  };
}

// MUST stay in sync with applyStretchPlan's exterior cascade — both call sites must produce identical exterior output. Do not "optimize" by short-circuiting when only items changed; that re-introduces the second-stretch ghost-line bug.
export function buildExteriorPatch(exterior, plan) {
  if (!exterior) return undefined;
  const newExteriorWallById = new Map(
    (plan.derivedExteriorWalls || []).map((e) => [e.wallId, e.after])
  );
  const newExteriorItemByKey = new Map();
  for (const entry of plan.affectedItems || []) {
    if (entry.scope === 'exterior') {
      newExteriorItemByKey.set(`${entry.kind}:${entry.id}`, entry.after);
    }
  }
  const hasAnyChange =
    newExteriorWallById.size > 0 || newExteriorItemByKey.size > 0;
  if (!hasAnyChange) return undefined;

  const next = { ...exterior };
  if (Array.isArray(exterior.walls)) {
    next.walls = exterior.walls.map((w) =>
      w && newExteriorWallById.has(w.id) ? newExteriorWallById.get(w.id) : w
    );
  }
  for (const arrayName of ['doors', 'windows', 'openings']) {
    const arr = exterior[arrayName];
    if (!Array.isArray(arr)) continue;
    next[arrayName] = arr.map((item) => {
      if (!item) return item;
      const k = `${arrayName.slice(0, -1)}:${item.id}`;
      return newExteriorItemByKey.has(k) ? newExteriorItemByKey.get(k) : item;
    });
  }
  return next;
}

/**
 * Substitute items in a child array (doors / windows / openings / objects)
 * using the plan's affectedItems entries that match `scope` (`room`,
 * `structure`, or `exterior`). For room scope, supply the `roomId` so the
 * composite key matches the room-scoped entries.
 *
 * `affectedItems` entries don't carry a `room`/`structure` scope tag today
 * — they're discriminated by `roomId` (set for room scope, null for
 * structure scope). Exterior items carry an explicit `scope: 'exterior'`.
 */
export function applyChildSubstitution(
  arr,
  arrayName,
  plan,
  { scope, roomId }
) {
  if (!Array.isArray(arr)) return arr;
  const kind = arrayName.slice(0, -1);
  const entryScope = (entry) =>
    entry.scope === 'exterior'
      ? 'exterior'
      : entry.roomId
        ? 'room'
        : 'structure';
  const newByKey = new Map();
  for (const entry of plan.affectedItems || []) {
    if (entry.kind !== kind) continue;
    if (entryScope(entry) !== scope) continue;
    if (scope === 'room' && entry.roomId !== roomId) continue;
    if (scope === 'exterior') {
      newByKey.set(`${entry.kind}:${entry.id}`, entry.after);
    } else {
      newByKey.set(
        `${entry.roomId || ''}:${entry.kind}:${entry.id}`,
        entry.after
      );
    }
  }
  if (newByKey.size === 0) return arr;
  return arr.map((item) => {
    if (!item) return item;
    const k =
      scope === 'exterior'
        ? `${kind}:${item.id}`
        : `${roomId || ''}:${kind}:${item.id}`;
    return newByKey.has(k) ? newByKey.get(k) : item;
  });
}

/**
 * Shift a room's `ceiling` array (facets + gap-fills + vertical bridges)
 * by a rigid XZ translation. Used for "translate" rooms in the stretch
 * save path — the ceiling rides along with the wall/corner write so we
 * don't have to round-trip through the regenerateCeilings callable for
 * rooms whose topology didn't change.
 *
 * For a pure XZ translation by `(delta * dir.x, 0, delta * dir.z)`:
 *  - polygonCorners and center shift by the XZ delta (y untouched).
 *  - dimensions, id, wallIds, connectsRectangles, generatedFrom,
 *    generatedAt are translation-invariant.
 *
 * @param {Array} ceiling - room.ceilings array (or null/undefined).
 * @param {number} delta - signed stretch magnitude.
 * @param {{x:number,z:number}} dir - unit direction along which to shift.
 * @returns {Array|null|undefined} new array (same shape), or the input
 *   unchanged when there's nothing to shift.
 */
/**
 * Shift each section's `center` and `bounds` by the XZ delta. Mirrors
 * `shiftCeilingByDelta` — applied for the same translate-classified
 * rooms so room labels (drawn at `section.center` or `room.center`)
 * follow the wall/ceiling cascade.
 *
 * Defensive across the bounds shape variants in `roomPolygonUtils`:
 * array `[minX, minY, minZ, maxX, maxY, maxZ]`, `{min, max}` objects,
 * and flat `{minX, minZ, maxX, maxZ}`.
 */
export function shiftSectionsByDelta(sections, delta, dir) {
  if (!Array.isArray(sections) || sections.length === 0) return sections;
  if (!dir || delta === 0) return sections;
  const dx = delta * dir.x;
  const dz = delta * dir.z;
  if (dx === 0 && dz === 0) return sections;
  return sections.map((section) => {
    if (!section) return section;
    const next = { ...section };
    if (section.center) next.center = shiftXZPoint(section.center, dx, dz);
    if (section.bounds) next.bounds = shiftBounds(section.bounds, dx, dz);
    return next;
  });
}

// Shift `{x, y, z}` or `[x, y, z]` by (dx, dz); y stays.
function shiftXZPoint(p, dx, dz) {
  if (!p) return p;
  if (Array.isArray(p)) {
    const out = p.slice();
    out[0] = (p[0] || 0) + dx;
    if (p.length >= 3) out[2] = (p[2] || 0) + dz;
    return out;
  }
  return { ...p, x: (p.x || 0) + dx, z: (p.z || 0) + dz };
}

function shiftBounds(b, dx, dz) {
  if (Array.isArray(b) && b.length >= 6) {
    const out = b.slice();
    out[0] = (b[0] || 0) + dx;
    out[2] = (b[2] || 0) + dz;
    out[3] = (b[3] || 0) + dx;
    out[5] = (b[5] || 0) + dz;
    return out;
  }
  if (b && typeof b === 'object') {
    const next = { ...b };
    if (b.min) next.min = shiftXZPoint(b.min, dx, dz);
    if (b.max) next.max = shiftXZPoint(b.max, dx, dz);
    if ('minX' in b) next.minX = (b.minX || 0) + dx;
    if ('minZ' in b) next.minZ = (b.minZ || 0) + dz;
    if ('maxX' in b) next.maxX = (b.maxX || 0) + dx;
    if ('maxZ' in b) next.maxZ = (b.maxZ || 0) + dz;
    return next;
  }
  return b;
}

export function shiftCeilingByDelta(ceiling, delta, dir) {
  if (!Array.isArray(ceiling) || ceiling.length === 0) return ceiling;
  if (!dir || delta === 0) return ceiling;
  const dx = delta * dir.x;
  const dz = delta * dir.z;
  if (dx === 0 && dz === 0) return ceiling;

  const shiftPoint = (p) => {
    if (!p) return p;
    return { ...p, x: (p.x || 0) + dx, z: (p.z || 0) + dz };
  };
  return ceiling.map((entry) => {
    if (!entry) return entry;
    const next = { ...entry };
    if (Array.isArray(entry.polygonCorners)) {
      next.polygonCorners = entry.polygonCorners.map((c) => shiftPoint(c));
    }
    if (entry.center) {
      next.center = shiftPoint(entry.center);
    }
    return next;
  });
}

/**
 * Splice `plan.affectedStructureFloorCorners` into `structure.floors[]`
 * and recompute each touched floor's `center` (centroid of new corners)
 * and `dimensions.x/z` (bbox of new corners). Y is preserved on both.
 *
 * Returns the new floors array, or the input `floors` ref unchanged when
 * the plan has no structure-floor corner entries (cheap no-op for the
 * common case of stretches that don't intersect the structure floor).
 *
 * The IFC and DXF converters read `structure.floors[].polygonCorners`
 * + `center` + `dimensions` directly when `source === 'structure'` or
 * `'both'` (default). Without this splice, exports emit a stale floor
 * slab next to the (correctly cascaded) per-room floors.
 */
export function applyStructureFloorCorners(floors, plan) {
  if (!Array.isArray(floors) || floors.length === 0) return floors;
  const entries = plan.affectedStructureFloorCorners || [];
  if (entries.length === 0) return floors;

  // Group entries by floor index.
  const byFloor = new Map();
  for (const e of entries) {
    if (!byFloor.has(e.floorIndex)) byFloor.set(e.floorIndex, new Map());
    byFloor.get(e.floorIndex).set(e.cornerIndex, e.after);
  }

  return floors.map((floor, fi) => {
    const cornerMap = byFloor.get(fi);
    if (!cornerMap || !floor || !Array.isArray(floor.polygonCorners)) {
      return floor;
    }
    const newCorners = floor.polygonCorners.map((corner, idx) => {
      if (!cornerMap.has(idx)) return corner;
      const after = cornerMap.get(idx);
      if (Array.isArray(corner)) return [after.x, corner[1], after.z];
      return { ...corner, x: after.x, z: after.z };
    });
    const next = { ...floor, polygonCorners: newCorners };
    // Recompute centroid (x/z) from the new corners; y preserved from the
    // original center.
    if (floor.center) {
      let sx = 0;
      let sz = 0;
      let n = 0;
      for (const c of newCorners) {
        const xz = getXZ(c);
        if (!xz) continue;
        sx += xz.x;
        sz += xz.z;
        n += 1;
      }
      if (n > 0) {
        next.center = { ...floor.center, x: sx / n, z: sz / n };
      }
    }
    // Recompute bbox dimensions (x/z); y preserved.
    if (floor.dimensions) {
      let minX = Infinity;
      let maxX = -Infinity;
      let minZ = Infinity;
      let maxZ = -Infinity;
      for (const c of newCorners) {
        const xz = getXZ(c);
        if (!xz) continue;
        if (xz.x < minX) minX = xz.x;
        if (xz.x > maxX) maxX = xz.x;
        if (xz.z < minZ) minZ = xz.z;
        if (xz.z > maxZ) maxZ = xz.z;
      }
      if (isFinite(minX) && isFinite(minZ)) {
        next.dimensions = {
          ...floor.dimensions,
          x: maxX - minX,
          z: maxZ - minZ,
        };
      }
    }
    return next;
  });
}

// distance stamp depends on post-plan structure shape — caller MUST pass the fully-spliced structure, not the pre-stretch one.
export function applyLockUpdates(currentLocks, plan, postPlanStructure) {
  const newLocks = { ...(currentLocks || {}) };
  for (const k of plan.lockUpdates?.remove || []) {
    delete newLocks[k];
  }
  for (const k of plan.lockUpdates?.add || []) {
    const distance = lookupLockedDistance(postPlanStructure, k);
    if (distance !== null) newLocks[k] = distance;
  }
  return newLocks;
}

/**
 * Pure substitution: walks the plan's before/after lists and swaps fields
 * on a clone of the structure. Does NOT re-run classification.
 */
export function applyStretchPlan(structureData, plan) {
  const newWallById = new Map();
  for (const entry of plan.affectedStructureWalls) {
    newWallById.set(entry.wallId, entry.after);
  }

  const newRoomWallByKey = new Map();
  for (const entry of plan.derivedRoomWalls) {
    newRoomWallByKey.set(`${entry.roomId}:${entry.wallId}`, entry.after);
  }

  const newCornerByKey = new Map();
  for (const entry of plan.affectedPolygonCorners) {
    newCornerByKey.set(`${entry.roomId}:${entry.cornerIndex}`, entry.after);
  }

  const newRooms = (structureData.rooms || []).map((room) => {
    const next = { ...room };

    next.walls = (room.walls || []).map((wall) => {
      const k = `${room.id}:${wall.id}`;
      return newRoomWallByKey.has(k) ? newRoomWallByKey.get(k) : wall;
    });

    if (room.floors && room.floors.length > 0) {
      next.floors = room.floors.map((floor, fi) => {
        if (fi !== 0 || !floor.polygonCorners) return floor;
        return {
          ...floor,
          polygonCorners: floor.polygonCorners.map((corner, idx) => {
            const k = `${room.id}:${idx}`;
            if (!newCornerByKey.has(k)) return corner;
            const after = newCornerByKey.get(k);
            if (Array.isArray(corner)) return [after.x, after.z];
            return { ...corner, x: after.x, z: after.z };
          }),
        };
      });
    }

    for (const arrayName of CHILD_ARRAYS) {
      if (!room[arrayName]) continue;
      next[arrayName] = applyChildSubstitution(
        room[arrayName],
        arrayName,
        plan,
        {
          scope: 'room',
          roomId: room.id,
        }
      );
    }

    // Translate rooms ride along with the wall/corner shift — their
    // ceilings topology doesn't change, so we apply the XZ shift inline
    // instead of regenerating server-side. Reshape rooms are left
    // untouched here; the caller fires regenerateCeilings for them.
    // Room labels live at `room.center` and/or `room.sections[].center`,
    // which aren't otherwise touched by the cascade — shift them by the
    // same delta so labels travel with the geometry.
    if (plan.roomClassification?.translate?.has(room.id)) {
      if (room.ceilings) {
        next.ceilings = shiftCeilingByDelta(
          room.ceilings,
          plan.delta,
          plan.direction
        );
      }
      if (room.center) {
        const dx = plan.delta * plan.direction.x;
        const dz = plan.delta * plan.direction.z;
        next.center = shiftXZPoint(room.center, dx, dz);
      }
      if (Array.isArray(room.sections)) {
        next.sections = shiftSectionsByDelta(
          room.sections,
          plan.delta,
          plan.direction
        );
      }
      // floors[].center.x/z drives fixtureTextureRegionUtils' floor-bbox.
      // polygonCorners were already shifted in next.floors above via
      // affectedPolygonCorners; read from next.floors (not room.floors)
      // so we don't drop that update. prealignment-* is a historical
      // snapshot — left untouched on purpose.
      const floorsSrc = Array.isArray(next.floors) ? next.floors : room.floors;
      if (Array.isArray(floorsSrc) && floorsSrc.length > 0) {
        const dx = plan.delta * plan.direction.x;
        const dz = plan.delta * plan.direction.z;
        next.floors = floorsSrc.map((floor) => {
          if (!floor || !floor.center) return floor;
          return {
            ...floor,
            center: shiftXZPoint(floor.center, dx, dz),
          };
        });
      }
    }

    return next;
  });

  const newStructure = {
    ...structureData,
    walls: (structureData.walls || []).map((wall) =>
      newWallById.has(wall.id) ? newWallById.get(wall.id) : wall
    ),
    rooms: newRooms,
  };

  if (Array.isArray(structureData.floors)) {
    const newFloors = applyStructureFloorCorners(structureData.floors, plan);
    if (newFloors !== structureData.floors) {
      newStructure.floors = newFloors;
    }
  }

  for (const arrayName of CHILD_ARRAYS) {
    if (!structureData[arrayName]) continue;
    newStructure[arrayName] = applyChildSubstitution(
      structureData[arrayName],
      arrayName,
      plan,
      { scope: 'structure' }
    );
  }

  if (structureData.exterior) {
    const patch = buildExteriorPatch(structureData.exterior, plan);
    if (patch !== undefined) {
      newStructure.exterior = patch;
    }
  }

  newStructure.measurementLocks = applyLockUpdates(
    structureData.measurementLocks,
    plan,
    newStructure
  );

  return newStructure;
}

/** One-line human-readable summary for the preview popover. */
export function summarizeStretchPlan(plan) {
  if (!plan) return '';
  const stretches = plan.affectedStructureWalls.filter(
    (w) => w.kind === 'stretch'
  ).length;
  const translates = plan.affectedStructureWalls.filter(
    (w) => w.kind === 'translate'
  ).length;
  const items = plan.affectedItems.length;
  const parts = [];
  if (stretches) {
    parts.push(`${stretches} wall${stretches === 1 ? '' : 's'} will resize`);
  }
  if (translates) {
    parts.push(`${translates} wall${translates === 1 ? '' : 's'} will shift`);
  }
  if (items) parts.push(`${items} item${items === 1 ? '' : 's'} will move`);
  if (plan.lockUpdates?.add?.length) {
    parts.push(
      `${plan.lockUpdates.add.length} measurement will be auto-locked`
    );
  }
  if (plan.lockUpdates?.remove?.length) {
    parts.push(
      `${plan.lockUpdates.remove.length} lock${
        plan.lockUpdates.remove.length === 1 ? '' : 's'
      } will be removed`
    );
  }
  return parts.length === 0 ? 'No changes.' : parts.join(', ') + '.';
}

/**
 * Forbidden boundary intervals for the proposed Δ.
 *
 * As the user drags the static/moving divider, walls parallel to the
 * stretch axis whose endpoints straddle the divider become "bridges":
 * one end stays static, the other end translates by Δ along the axis.
 * For a shrink (Δ < 0) the bridge length goes from `L` to `L + Δ`. The
 * boundary must NOT sit anywhere that would shrink a bridge below
 * `MIN_WALL_LENGTH`, i.e. inside the projection range `[lo, hi]` of
 * any parallel wall whose `(hi − lo) < |Δ| + MIN_WALL_LENGTH`.
 *
 * Expansion (Δ ≥ 0) makes bridges grow — no Δ-dependent zones returned.
 * Tilted walls aren't included; they raise a non-blocking `tilted-wall`
 * warning after the fact and the boundary isn't constrained around them.
 *
 * The edited wall is excluded — its own length is checked separately by
 * the input validator (`MIN_WHOLE_WALL_M`), and by definition the
 * boundary always lives strictly inside its projection range.
 *
 * @param {Object} structureData - { walls, rooms, ... }.
 * @param {Object} measurement - The wall-length measurement being edited.
 * @param {number} delta - Proposed change (negative for shrink).
 * @returns {Array<{ range: [number, number], wallId: string,
 *   currentLength: number, reason: 'wall-would-collapse' }>}
 */
export function computeBoundaryClampZones(structureData, measurement, delta) {
  if (!structureData || !measurement) return [];
  if (!Number.isFinite(delta) || delta >= 0) return [];

  const axis = getMeasurementAxis(measurement);
  if (axis.error) return [];
  const dir = axis.dir;

  const editedStructureWall = findStructureWallByRoomWallId(
    structureData,
    measurement.wallId
  );
  const editedStructureWallId = editedStructureWall?.id || null;

  const required = -delta + MIN_WALL_LENGTH;
  const zones = [];
  for (const wall of structureData.walls || []) {
    if (!wall || !wall.center || !wall.dimensions) continue;
    if (wall.id === editedStructureWallId) continue;
    const wallDir = apply3DRotationTo2D(wall.rotation);
    const parallel =
      Math.abs(wallDir[0] * dir.x + wallDir[1] * dir.z) > PARALLEL_DOT;
    if (!parallel) continue;
    const ends = wallEndpointsXZ(wall);
    const aProj = ends.a.x * dir.x + ends.a.z * dir.z;
    const bProj = ends.b.x * dir.x + ends.b.z * dir.z;
    const lo = Math.min(aProj, bProj);
    const hi = Math.max(aProj, bProj);
    const wallLen = hi - lo;
    if (wallLen >= required) continue;
    zones.push({
      range: [lo, hi],
      wallId: wall.id,
      currentLength: wallLen,
      reason: 'wall-would-collapse',
    });
  }
  return zones;
}

/**
 * Forbidden boundary intervals derived from *locked* measurements.
 *
 * A locked wall keeps its length only while the static/moving divider
 * doesn't straddle it: if the boundary sits strictly inside the locked
 * wall's projection range `[lo, hi]` (one end static, one end moving) the
 * wall stretches and the lock is violated. If the whole locked wall is on
 * one side it either stays put or translates rigidly — length preserved.
 *
 * So each locked wall parallel to `dir` contributes its `[lo, hi]` range as
 * a forbidden zone. The hook snaps the boundary out of these (toward the
 * locked wall's *inner* edge — the partition shared with the unlocked
 * sibling), so editing the opposite side leaves the locked room fixed and
 * the adjacent unlocked room absorbs the change. When no valid boundary
 * exists (the locked wall spans the whole edited range, or two locks leave
 * no gap) the post-hoc lock-conflict check still blocks, with the Unlock
 * fallback.
 *
 * Locks on walls perpendicular to `dir` are ignored — a stretch along `dir`
 * doesn't change their (perpendicular) length, so they never conflict.
 * Only `wall:` locks are considered (the same scope the lock-conflict check
 * supports); `partial:` sub-span locks aren't resolved here.
 *
 * @param {Object} structureData - { rooms, measurementLocks, ... }.
 * @param {Object} measurement - The wall-length measurement being edited.
 * @returns {Array<{ range: [number, number], wallId: string, lockKey: string }>}
 */
export function computeLockedBoundaryZones(structureData, measurement) {
  if (!structureData?.measurementLocks || !measurement) return [];
  const locks = Object.keys(structureData.measurementLocks);
  if (locks.length === 0) return [];

  const axis = getMeasurementAxis(measurement);
  if (axis.error) return [];
  const dir = axis.dir;
  const editedKey = getMeasurementKey(measurement);

  // Index room walls by id — `wall:` locks key on a room-wall id, and the
  // lock value is that room wall's length (see lookupLockedDistance).
  const roomWallById = new Map();
  for (const room of structureData.rooms || []) {
    for (const wall of room.walls || []) {
      if (wall?.id) roomWallById.set(wall.id, wall);
    }
  }

  const zones = [];
  for (const key of locks) {
    if (key === editedKey) continue;
    if (!key.startsWith('wall:')) continue;
    const wall = roomWallById.get(key.slice('wall:'.length));
    if (!wall || !wall.center || !wall.dimensions) continue;
    const wallDir = apply3DRotationTo2D(wall.rotation);
    const parallel =
      Math.abs(wallDir[0] * dir.x + wallDir[1] * dir.z) > PARALLEL_DOT;
    if (!parallel) continue;
    const ends = wallEndpointsXZ(wall);
    const aProj = ends.a.x * dir.x + ends.a.z * dir.z;
    const bProj = ends.b.x * dir.x + ends.b.z * dir.z;
    const lo = Math.min(aProj, bProj);
    const hi = Math.max(aProj, bProj);
    if (hi - lo < 1e-6) continue;
    zones.push({ range: [lo, hi], wallId: wall.id, lockKey: key });
  }
  return zones;
}
